/**
 * v-number-blur — 全局指令，自动给 el-input-number 加失焦校验
 *
 * 用法（最简）：
 *   <el-input-number v-model="form.amount" :min="0" :precision="2" v-number-blur />
 *
 * 用法（显式覆盖约束）：
 *   <el-input-number v-model="form.amount" v-number-blur="{ min: 0, precision: 2, allowNegative: true }" />
 *
 * 默认约束从 Vue 组件 props 读取（Element Plus 内部不把 precision 渲染到 DOM），
 * 因此绝大多数场景无需传 opts。
 */
import type { Directive, DirectiveBinding } from 'vue'
import { ElMessage } from 'element-plus'
import type { NumberBlurOptions } from '@/utils/useNumberInputBlur'

function toNumber(v: unknown): number {
  if (v === null || v === undefined || v === '') return NaN
  if (typeof v === 'number') return v
  const s = String(v).trim()
  if (s === '') return NaN
  const n = Number(s)
  return n
}

/**
 * 校验 + 回写 + 弹错
 * 返回 true 表示通过；false 表示已修正
 */
function doValidate(el: HTMLInputElement, opts: NumberBlurOptions): boolean {
  const raw = el.value
  // 空值：默认允许
  if (raw === '' || raw === null) {
    if (opts.allowEmpty === false) {
      ElMessage.error('该项不能为空')
      el.value = ''
      el.dispatchEvent(new Event('input', { bubbles: true }))
      return false
    }
    return true
  }

  const n = toNumber(raw)
  if (Number.isNaN(n) || !Number.isFinite(n)) {
    ElMessage.error('请输入有效数字')
    el.value = ''
    el.dispatchEvent(new Event('input', { bubbles: true }))
    return false
  }

  // 负数
  if (opts.allowNegative !== true && n < 0) {
    const min = opts.min ?? 0
    ElMessage.error(`不能小于 ${min}`)
    el.value = String(min)
    el.dispatchEvent(new Event('input', { bubbles: true }))
    return false
  }

  // min
  if (opts.min !== undefined && n < opts.min) {
    ElMessage.error(`不能小于 ${opts.min}`)
    el.value = String(opts.min)
    el.dispatchEvent(new Event('input', { bubbles: true }))
    return false
  }

  // max
  if (opts.max !== undefined && n > opts.max) {
    ElMessage.error(`不能大于 ${opts.max}`)
    el.value = String(opts.max)
    el.dispatchEvent(new Event('input', { bubbles: true }))
    return false
  }

  // precision（用字符串判小数位，避开浮点误差）
  if (opts.precision !== undefined && opts.precision >= 0) {
    const str = String(n)
    const dotIdx = str.indexOf('.')
    if (dotIdx >= 0) {
      const decimals = str.length - dotIdx - 1
      if (decimals > opts.precision) {
        const fixed = Number(n.toFixed(opts.precision))
        ElMessage.warning(`最多保留 ${opts.precision} 位小数`)
        el.value = String(fixed)
        el.dispatchEvent(new Event('input', { bubbles: true }))
        return false
      }
    }
  }

  return true
}

/**
 * 从 el-input-number 组件实例读 min/max/precision props
 */
function readPropsFromInstance(el: HTMLElement): { min?: number; max?: number; precision?: number } {
  // Element Plus 把所有 props 暴露在 __vueParentComponent.props（dev 模式）或组件 vnode 上
  // 实战中可用最稳的方式：el-input-number 渲染根是 .el-input-number 元素，但
  // 该元素本身是组件实例的 $el —— 但 $el 在 directive context 里不可直接拿。
  // 用 (el as any).__vueParentComponent 拿到组件实例，进而拿 props。
  const root = (el.classList.contains('el-input-number') ? el : el.querySelector('.el-input-number')) as any
  if (!root) return {}
  // 遍历组件实例链查找 el-input-number
  let inst: any = (root as any).__vueParentComponent
  let foundProps: any = null
  // 尝试组件自身 + 上溯
  for (let i = 0; i < 5 && inst; i++) {
    const name = (inst.type as any)?.name
    if (name === 'ElInputNumber' || (inst?.proxy?.$options?.name === 'ElInputNumber')) {
      foundProps = inst.props
      break
    }
    inst = inst.parent
  }
  if (!foundProps) return {}
  const out: { min?: number; max?: number; precision?: number } = {}
  if (Number.isFinite(foundProps.min)) out.min = Number(foundProps.min)
  if (Number.isFinite(foundProps.max)) out.max = Number(foundProps.max)
  if (Number.isFinite(foundProps.precision)) out.precision = Number(foundProps.precision)
  return out
}

function readOpts(el: HTMLElement, override?: NumberBlurOptions): NumberBlurOptions {
  const fromProps = readPropsFromInstance(el)
  return {
    min: override?.min ?? fromProps.min,
    max: override?.max ?? fromProps.max,
    precision: override?.precision ?? fromProps.precision,
    allowNegative: override?.allowNegative,
    allowEmpty: override?.allowEmpty,
  }
}

function findInput(target: HTMLElement): HTMLInputElement | null {
  // 当前元素是 .el-input-number，直接找内部 input
  const root = target.classList.contains('el-input-number')
    ? target
    : target.querySelector('.el-input-number')
  if (root) {
    const inp = root.querySelector('input')
    if (inp) return inp as HTMLInputElement
  }
  if (target instanceof HTMLInputElement) return target
  return null
}

function makeHandler(opts: NumberBlurOptions) {
  return function handler(this: HTMLInputElement) {
    doValidate(this, opts)
  }
}

export const vNumberBlur: Directive<HTMLElement, NumberBlurOptions | undefined> = {
  mounted(el: HTMLElement, binding: DirectiveBinding<NumberBlurOptions | undefined>) {
    const override = binding.value
    const opts = readOpts(el, override)
    const input = findInput(el)
    if (!input) return
    const handler = makeHandler(opts)
    ;(input as any).__numberBlurHandler__ = handler
    ;(input as any).__numberBlurOpts__ = opts
    input.addEventListener('blur', handler)
  },
  updated(el: HTMLElement, binding: DirectiveBinding<NumberBlurOptions | undefined>) {
    const override = binding.value
    const opts = readOpts(el, override)
    const input = findInput(el)
    if (!input) return
    // 解绑旧 handler，挂新 handler（opts 可能变化）
    const oldHandler = (input as any).__numberBlurHandler__
    if (oldHandler) input.removeEventListener('blur', oldHandler)
    const handler = makeHandler(opts)
    ;(input as any).__numberBlurHandler__ = handler
    ;(input as any).__numberBlurOpts__ = opts
    input.addEventListener('blur', handler)
  },
  unmounted(el: HTMLElement) {
    const input = findInput(el)
    if (!input) return
    const handler = (input as any).__numberBlurHandler__
    if (handler) input.removeEventListener('blur', handler)
  },
}

export function registerNumberBlur(app: any) {
  app.directive('number-blur', vNumberBlur)
}